import React, { useState, useEffect, useCallback, useRef } from 'react';
import BackButton from './BackButton';

export interface PageBackButtonProps {
  text?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * PageBackButton
 * A DOM-aware back button that adjusts its position dynamically below
 * the navigation and optional hero sections.
 *
 * Key Features:
 * - Dynamically measures nav and hero heights
 * - React.memo and useCallback for performance
 * - ResizeObserver for responsive updates
 * - No animations (instant reposition)
 * - Retry mechanism for initial page load
 */
const PageBackButton: React.FC<PageBackButtonProps> = ({
  text = 'Home',
  onClick,
  className = '',
}) => {
  const [spacing, setSpacing] = useState(0);
  const navRef = useRef<HTMLElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);

  const calculateSpacing = useCallback(() => {
    requestAnimationFrame(() => {
      const nav = navRef.current ?? document.querySelector('nav');
      if (!nav) return;

      const navHeight = nav.offsetHeight || 0;
      const heroBar = heroRef.current ?? (nav.previousElementSibling as HTMLElement | null);
      const heroHeight =
        heroBar && (heroBar.children.length > 0 || heroBar.textContent?.trim())
          ? heroBar.scrollHeight || 0
          : 0;

      const total = navHeight + heroHeight;
      
      // Only update state if value actually changed
      setSpacing((prev) => (prev !== total ? total : prev));

      console.debug('[PageBackButton] spacing updated:', {
        navHeight,
        heroHeight,
        total,
      });
    });
  }, []);

  useEffect(() => {
    // Retry mechanism for initial page load
    let retries = 0;
    const maxRetries = 10;
    let retryTimeout: number;

    const tryInitialize = () => {
      const nav = document.querySelector('nav');
      if (nav && nav.offsetHeight > 0) {
        // Nav is ready
        navRef.current = nav;
        heroRef.current = nav.previousElementSibling as HTMLElement | null;
        calculateSpacing();
      } else if (retries < maxRetries) {
        // Nav not ready yet, retry
        retries++;
        console.debug(`[PageBackButton] Nav not ready, retry ${retries}/${maxRetries}`);
        retryTimeout = setTimeout(tryInitialize, 100);
      } else {
        console.warn('[PageBackButton] Max retries reached, nav element not found');
      }
    };

    // Start initialization
    tryInitialize();

    // Set up ResizeObserver for efficient size tracking
    const resizeObserver = new ResizeObserver(calculateSpacing);
    if (navRef.current) resizeObserver.observe(navRef.current);
    if (heroRef.current) resizeObserver.observe(heroRef.current);

    // Fallback resize listener
    window.addEventListener('resize', calculateSpacing);

    return () => {
      clearTimeout(retryTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateSpacing);
    };
  }, [calculateSpacing]);

  return (
    <div className={className} style={{ marginTop: `${spacing}px` }}>
      <BackButton text={text} onClick={onClick} />
    </div>
  );
};

export default React.memo(PageBackButton);
